import React from 'react';
import { Settings, RefreshCw, CheckCircle, ArrowRight, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { IntelligenceProfile } from '../../types';

interface ProfileCardActionsProps {
  profile: IntelligenceProfile;
  executingOrchestrator: string | null;
  isProfileProcessing: (profile: IntelligenceProfile) => boolean;
  onExecuteOrchestrator: (profile: IntelligenceProfile) => void;
  onReprocessAnalysis?: (profile: IntelligenceProfile) => void;
  className?: string;
}

export const ProfileCardActions: React.FC<ProfileCardActionsProps> = ({
  profile,
  executingOrchestrator,
  isProfileProcessing,
  onExecuteOrchestrator,
  onReprocessAnalysis,
  className = ""
}) => {
  // Check if analysis has been submitted (look for tracking_id)
  const hasBeenSubmitted = profile.tracking_id && profile.submission;
  const processingStatus = profile.submission?.processing_status;

  // If already submitted, show appropriate status
  if (hasBeenSubmitted) {
    const canReprocess = (processingStatus === 'completed' || processingStatus === 'failed') && onReprocessAnalysis;

    return (
      <div className={`flex items-center justify-center pt-4 border-t border-gray-100 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
            <CheckCircle className="h-4 w-4" />
            <span>
              {processingStatus === 'pending' && 'Analysis Queued'}
              {processingStatus === 'processing' && 'Processing in Background'}
              {processingStatus === 'completed' && 'Analysis Complete'}
              {processingStatus === 'failed' && 'Analysis Failed'}
              {(!processingStatus || processingStatus === 'submitted') && 'Analysis Queued'}
            </span>
          </div>

          {canReprocess && (
            <Button
              onClick={() => onReprocessAnalysis(profile)}
              disabled={executingOrchestrator === profile.id}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400"
            >
              <RotateCcw className="h-3 w-3" />
              Re-process
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center pt-4 border-t border-gray-100 ${className}`}>
      <Button
        onClick={() => onExecuteOrchestrator(profile)}
        disabled={executingOrchestrator === profile.id}
        size="sm"
        className={`flex items-center transition-all duration-200 ${
          executingOrchestrator === profile.id
            ? 'bg-blue-600 hover:bg-blue-700'
            : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
        }`}
      >
        {executingOrchestrator === profile.id ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Queuing Analysis...
          </>
        ) : (
          <>
            <Settings className="h-4 w-4 mr-2" />
            Queue Analysis
            <ArrowRight className="h-4 w-4 ml-1" />
          </>
        )}
      </Button>
    </div>
  );
};